import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { useAppDispatch } from "app/hooks";
import {
  clearSsoSession,
  fetchCurrentUser,
  setToken
} from "features/auth/auth-slice";
import {
  SSO_TOKEN_TTL_MS,
  readHashParam,
  ssoErrorMessage
} from "utils/sso.util";

import SsoErrorView from "./sso-error-view.component";

/**
 * SSO landing (matched to krish/eng-1411-auth-backbone). The backend runs the
 * whole OAuth exchange server-side and redirects here with the result in the URL
 * hash — /sso/complete#auth_token=<hex> on success, /sso/error#sso_error=<code>
 * on failure. One component serves both routes (the route is decided by which
 * hash param is present).
 *
 * On success we still fail closed (tech-plan D3): store the token like
 * loginUser's keep-logged-in path (token + expiration → localStorage, so
 * RequireAuth rehydrates — D1/D2), then REQUIRE a usable session (a user with a
 * practice) before entering the app. Anything else clears all auth.
 */
export default function SsoCallback() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [error, setError] = useState<string>("");
  const [linked, setLinked] = useState(false);
  // Capture the hash EXACTLY ONCE. The SSO landing is always reached via a
  // full-page backend redirect, so the hash is present at first render. We do
  // NOT scrub the hash: dispatching setToken re-renders the route tree and can
  // re-mount this component, and a re-mounted effect that re-read a scrubbed
  // hash would see no token and render a spurious "couldn't sign you in" error.
  // navigate(replace:true) already keeps the token out of the back-button
  // history on success, so scrubbing was both unnecessary and harmful.
  const [{ ssoError, authToken, linkConfirmed }] = useState(() => ({
    ssoError: readHashParam("sso_error"),
    authToken: readHashParam("auth_token"),
    linkConfirmed: readHashParam("linked") === "1"
  }));
  const ran = useRef(false); // guard double-invoke within a mount

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const run = async () => {
      // Error landing: the backend already decided it failed.
      if (ssoError) {
        setError(ssoErrorMessage(ssoError));
        return;
      }
      if (!authToken) {
        setError(ssoErrorMessage());
        return;
      }

      // Persist like loginUser's keep-logged-in path so RequireAuth rehydrates.
      const expiration = (Date.now() + SSO_TOKEN_TTL_MS).toString();
      localStorage.setItem("token", authToken);
      localStorage.setItem("expiration", expiration);
      dispatch(setToken({ token: authToken }));

      // Fail closed: require a user WITH a practice. The backend should already
      // guarantee this, but a hiccup must never leave a half-authenticated
      // session that RequireAuth rehydrates into an empty dashboard.
      const me = await dispatch(fetchCurrentUser({ token: authToken }));
      const hasPractice =
        fetchCurrentUser.fulfilled.match(me) && Boolean(me.payload?.practice);
      if (!hasPractice) {
        await dispatch(clearSsoSession());
        setError(ssoErrorMessage("no_practice"));
        return;
      }

      if (linkConfirmed) {
        setLinked(true); // hold on the confirmation; user continues explicitly
        return;
      }
      navigate("/", { replace: true });
    };

    void run();
  }, [dispatch, navigate, ssoError, authToken, linkConfirmed]);

  if (error) {
    return <SsoErrorView message={error} />;
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
      <Stack gap={3} alignItems="center" sx={{ maxWidth: 420, width: "100%" }}>
        {linked ? (
          <>
            <Alert
              icon={false}
              severity="success"
              data-testid="sso-callback-linked"
              sx={{ width: "100%" }}
            >
              We&apos;ve linked your account to your existing Suno profile.
            </Alert>
            <Button
              variant="contained"
              fullWidth
              onClick={() => navigate("/", { replace: true })}
            >
              Continue
            </Button>
          </>
        ) : (
          <>
            <CircularProgress />
            <Typography variant="subtitle1">Signing you in…</Typography>
          </>
        )}
      </Stack>
    </Box>
  );
}
